import { zodResolver } from '@hookform/resolvers/zod'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

import TabHeader from '@/app/component/atom/tab-header'
import { FormField } from '@/app/component/form/field'
import SubmitButton from '@/app/component/form/submit-button'
import { useChangePassword } from '@/app/module/user/hooks/use-change-password'

type ChangePasswordFormValues = {
  currentPassword: string
  newPassword: string
  newPasswordConfirm: string
}

const ResetPassword: React.FC = () => {
  const [changePassword, { isLoading }] = useChangePassword()

  const methods = useForm<ChangePasswordFormValues>({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      newPasswordConfirm: '',
    },
    mode: 'all',
    resolver: zodResolver(schema),
  })

  const currentPassword = methods.watch('currentPassword')
  const newPassword = methods.watch('newPassword')
  const newPasswordConfirm = methods.watch('newPasswordConfirm')

  React.useEffect(() => {
    if (methods.formState.touchedFields.newPassword && currentPassword) {
      methods.trigger('newPassword')
    }
  }, [currentPassword, methods])

  React.useEffect(() => {
    if (methods.formState.touchedFields.newPasswordConfirm && newPassword) {
      methods.trigger('newPasswordConfirm')
    }
  }, [newPassword, methods])

  React.useEffect(() => {
    if (methods.formState.touchedFields.newPassword && newPasswordConfirm) {
      methods.trigger('newPassword')
    }
  }, [newPasswordConfirm, methods])

  const handleSubmit = async (data: ChangePasswordFormValues) => {
    await changePassword(data)
  }

  return (
    <Card>
      <TabHeader title="Password Reset" />
      <CardContent>
        <FormProvider {...methods}>
          <form noValidate onSubmit={methods.handleSubmit(handleSubmit)}>
            <FormField
              type="password"
              fieldProps={{ label: 'Current Password', name: 'currentPassword', required: true }}
            />
            <FormField type="password" fieldProps={{ label: 'New Password', name: 'newPassword', required: true }} />
            <FormField
              type="password"
              fieldProps={{ label: 'Confirm New Password', name: 'newPasswordConfirm', required: true }}
            />
            <CardActions sx={{ px: 0 }}>
              <SubmitButton isDirty={methods.formState.isDirty} isSubmitting={isLoading}>
                Save
              </SubmitButton>
            </CardActions>
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  )
}

const schema = z
  .object({
    currentPassword: z.string({ required_error: 'Current password is required' }).min(1),
    newPassword: z
      .string({ required_error: 'New password is required' })
      .min(15, { message: 'Password must be at least 15 characters long' }),
    newPasswordConfirm: z
      .string({ required_error: 'Confirm new password is required' })
      .min(15, { message: 'Password must be at least 15 characters long' }),
  })
  .refine((data) => data.newPassword !== data.currentPassword, {
    message: 'New password must be different from the current password',
    path: ['newPassword'],
  })
  .refine((data) => data.newPassword === data.newPasswordConfirm, {
    message: 'Passwords have to match',
    path: ['newPasswordConfirm'],
  })

export default ResetPassword
